import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  Chip
} from '@mui/material';
import DataTable from '../../../components/design/DataTable';
import LoadingState from '../../../components/design/LoadingState';
import EmptyState from '../../../components/design/EmptyState';
import pauseTypesService from '../../../services/pauseTypesService';

export default function PauseTypesPage() {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [dialog, setDialog] = useState({ open: false, mode: 'create', row: null });
  const [form, setForm] = useState({
    name: '',
    description: '',
    active: true,
    requiresNote: false,
    sortOrder: 0,
    category: '',
    color: ''
  });

  const columns = [
    { key: 'name', label: 'Nome' },
    { key: 'category', label: 'Categoria', render: (v) => v ? String(v) : '—' },
    { key: 'sortOrder', label: 'Ordem', align: 'right' },
    { key: 'requiresNote', label: 'Exige observação', render: (v) => (v ? 'Sim' : 'Não') },
    {
      key: 'active',
      label: 'Status',
      render: (v) => <Chip size="small" color={v ? 'success' : 'default'} label={v ? 'Ativo' : 'Inativo'} />
    },
    {
      key: 'actions',
      label: 'Ações',
      align: 'right',
      render: (_, r) => (
        <Button size="small" variant="outlined" onClick={() => openEdit(r)}>
          Editar
        </Button>
      )
    }
  ];

  const load = async () => {
    setLoading(true);
    try {
      const resp = await pauseTypesService.list();
      const data = resp?.data?.data || resp?.data;
      const list = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
      setRows(list);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load().catch(() => {}); }, []);

  const openCreate = () => {
    setDialog({ open: true, mode: 'create', row: null });
    setForm({ name: '', description: '', active: true, requiresNote: false, sortOrder: 0, category: '', color: '' });
  };

  const openEdit = (row) => {
    setDialog({ open: true, mode: 'edit', row });
    setForm({
      name: row?.name || '',
      description: row?.description || '',
      active: row?.active !== false,
      requiresNote: Boolean(row?.requiresNote),
      sortOrder: Number(row?.sortOrder || 0),
      category: row?.category || '',
      color: row?.color || ''
    });
  };

  const close = () => setDialog({ open: false, mode: 'create', row: null });

  const save = async () => {
    setLoading(true);
    try {
      if (dialog.mode === 'create') {
        await pauseTypesService.create({
          name: form.name,
          description: form.description || null,
          active: Boolean(form.active),
          requiresNote: Boolean(form.requiresNote),
          sortOrder: Number(form.sortOrder || 0),
          category: form.category || null,
          color: form.color || null
        });
      } else {
        await pauseTypesService.update(dialog.row.id, {
          name: form.name,
          description: form.description || null,
          active: Boolean(form.active),
          requiresNote: Boolean(form.requiresNote),
          sortOrder: Number(form.sortOrder || 0),
          category: form.category || null,
          color: form.color || null
        });
      }
      close();
      await load();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box p={3}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h5">Tipos de pausa</Typography>
        <Button variant="contained" onClick={openCreate}>Novo tipo</Button>
      </Box>

      {loading ? (
        <LoadingState label="Carregando tipos de pausa..." />
      ) : rows.length ? (
        <DataTable columns={columns} rows={rows} getKey={(r) => r.id} />
      ) : (
        <Paper>
          <EmptyState title="Nenhum tipo de pausa cadastrado" actionLabel="Novo tipo" onAction={openCreate} />
        </Paper>
      )}

      <Dialog open={dialog.open} onClose={close} maxWidth="sm" fullWidth>
        <DialogTitle>{dialog.mode === 'create' ? 'Novo tipo de pausa' : 'Editar tipo de pausa'}</DialogTitle>
        <DialogContent>
          <Box mt={1} display="grid" gap={2}>
            <TextField label="Nome" value={form.name} onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))} fullWidth />
            <TextField label="Descrição" value={form.description} onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))} fullWidth multiline minRows={2} />
            <TextField label="Ordem" type="number" value={form.sortOrder} onChange={(e) => setForm(prev => ({ ...prev, sortOrder: e.target.value }))} fullWidth />
            <TextField label="Categoria" value={form.category} onChange={(e) => setForm(prev => ({ ...prev, category: e.target.value }))} fullWidth />
            <TextField label="Cor (hex/opcional)" value={form.color} onChange={(e) => setForm(prev => ({ ...prev, color: e.target.value }))} fullWidth />
            <FormControlLabel control={<Switch checked={form.requiresNote} onChange={(e) => setForm(prev => ({ ...prev, requiresNote: e.target.checked }))} />} label="Exige observação" />
            <FormControlLabel control={<Switch checked={form.active} onChange={(e) => setForm(prev => ({ ...prev, active: e.target.checked }))} />} label="Ativo" />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={close}>Cancelar</Button>
          <Button variant="contained" onClick={save} disabled={!String(form.name || '').trim()}>Salvar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

